import { Injectable } from "@angular/core";
import { PreloadingStrategy, Route } from "@angular/router";
import { Observable, of, timer } from "rxjs";
import { mergeMap } from "rxjs/operators";

/**
 * Preloads only the lazy routes explicitly flagged with `data: { preload: true }`,
 * and defers that work until the browser is idle so it never competes with the
 * initial render / time-to-interactive.
 *
 * This replaces `PreloadAllModules`, which eagerly downloaded and parsed every
 * feature module (chart.js, ng-zorro, coreui, etc.) right after boot. Routes
 * without the flag stay truly lazy and load on first navigation.
 */
@Injectable({ providedIn: "root" })
export class SelectivePreloadStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (!route.data || route.data["preload"] !== true) {
      return of(null);
    }

    return this.whenIdle().pipe(mergeMap(() => load()));
  }

  /**
   * Emits once the browser is idle. Falls back to a short timer where
   * requestIdleCallback is unavailable (e.g. Safari).
   */
  private whenIdle(): Observable<void> {
    const ric = (globalThis as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;

    if (typeof ric !== "function") {
      return timer(2000) as unknown as Observable<void>;
    }

    return new Observable<void>((subscriber) => {
      ric(
        () => {
          subscriber.next();
          subscriber.complete();
        },
        { timeout: 5000 }
      );
    });
  }
}
